import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { srcSetFor } from "@/lib/responsive";
import { ArrowRight, Hammer, Paintbrush, Wrench, Shield, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionReveal from "@/components/SectionReveal";
import WaveDivider from "@/components/WaveDivider";
import CTAStrip from "@/components/CTAStrip";
import { useGalleryItems } from "@/hooks/useGalleryItems";
import hardwoodImg from "@/assets/gallery-hardwood-finished.jpg";
import ceramicImg from "@/assets/gallery-ceramic-install.jpg";
import laminateImg from "@/assets/gallery-laminate-install.jpg";
import hardTileImg from "@/assets/gallery-tile-after.jpg";
import marbleImg from "@/assets/marble-floor-installed.jpg";

const primaryServices = [
  { title: "Hardwood Flooring", desc: "Hardwood is our specialty — and has been for over 35 years. We install beautiful, durable hardwood floors that bring warmth and character to any room. From classic oak to modern finishes, we source premium materials and handle every step, from subfloor preparation to final finishing. The result is a floor that looks stunning and stands the test of time.", img: hardwoodImg, alt: "Hardwood floor installation in Barrington IL" },
  { title: "Ceramic Flooring", desc: "Ceramic tile is one of the most versatile and durable flooring options available. We install ceramic in kitchens, bathrooms, entryways, and any area that needs a surface built to handle moisture, traffic, and daily wear. With endless design possibilities — from classic subway patterns to modern large-format tiles — ceramic gives you both style and practicality.", img: ceramicImg, alt: "Ceramic tile floor installation" },
  { title: "Laminate Flooring", desc: "Laminate flooring offers the beauty of natural wood or stone at a fraction of the cost. It's a smart, affordable option for homeowners who want a polished look without the premium price tag. We install laminate quickly and precisely, with tight seams and a clean finish that transforms any room.", img: laminateImg, alt: "Laminate flooring installation" },
];

const whatToExpect = "Every project starts with an on-site visit where we assess your space, check the subfloor, and take accurate measurements. We buy all the materials ourselves — you don't need to coordinate with suppliers or make trips to the store. From there, our team handles the professional installation with careful attention to every detail, leaving you with a finished floor you'll love.";

const additionalServices = [
  { icon: Hammer, title: "Hard Tile", desc: "Expert hard tile installation with precision craftsmanship. Hard tile is ideal for high-traffic areas, outdoor transitions, and spaces that need a resilient, long-lasting surface. We handle the layout, cutting, and installation to ensure a clean, professional result.", img: hardTileImg, alt: "Hard tile flooring installation" },
  { icon: Paintbrush, title: "Marble Flooring", desc: "Marble brings a timeless elegance to any room. We source, cut, and install marble flooring with the care it deserves — every seam aligned, every surface polished. Whether it's a foyer, bathroom, or living area, marble adds a level of sophistication that's hard to match.", img: marbleImg, alt: "Marble floor installation" },
  { icon: Wrench, title: "Floor Repairs", desc: "Water damage, warping, or general wear — we handle it all. Our repair and restoration services bring damaged floors back to life. And we'll fix the flooring even if the damage wasn't caused by our work. That's how much we stand behind our craft.", img: hardwoodImg, alt: "Floor repair and restoration" },
];

const galleryCategories = ["All", "Hardwood", "Ceramic & Tile", "Laminate"] as const;

const Services = () => {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const { items: galleryItems } = useGalleryItems();
  return (
    <Layout>
      <SEOHead
        title="Flooring Services | JTB Floors — Barrington, IL"
        description="Professional hardwood, ceramic, laminate, hard tile & marble flooring in Barrington, IL. 35 years experience. Materials and labor included. Free estimates."
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h1 className="text-4xl md:text-5xl font-heading font-black mb-4">Flooring Services in Barrington, IL & Surrounding Areas</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">From hardwood and ceramic to laminate, tile, and marble — we've spent 35 years perfecting our craft. Every project includes materials, professional installation, and a satisfaction guarantee. We serve Barrington, IL and all areas within a 35-mile radius.</p>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* Primary Services */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">Primary Flooring Services</h2>
          </SectionReveal>
          <div className="space-y-16">
            {primaryServices.map((service, i) => (
              <SectionReveal key={i} delay={i * 0.1}>
                <div className={`flex flex-col ${i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-8 items-center`}>
                  <div className="lg:w-1/2">
                    <img src={service.img} srcSet={srcSetFor(service.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={800} height={600} decoding="async" alt={service.alt} className="rounded-xl shadow-lg w-full aspect-[4/3] object-cover" loading="lazy" />
                  </div>
                  <div className="lg:w-1/2">
                    <h3 className="text-2xl font-heading font-bold mb-3">{service.title}</h3>
                    <p className="text-muted-foreground mb-4 leading-relaxed">{service.desc}</p>
                    <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
                      <h4 className="font-semibold mb-2 text-sm">What to Expect:</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{whatToExpect}</p>
                    </div>
                    <Button asChild size="sm" className="btn-shine gap-2">
                      <Link to="/contact" hash="form">Request Quote <ArrowRight className="w-4 h-4" /></Link>
                    </Button>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--muted))" />

      {/* Additional Services */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">Additional Services</h2>
            <p className="text-center text-muted-foreground max-w-xl mx-auto mb-12">Beyond our primary flooring — we handle tile, marble, and repairs too.</p>
          </SectionReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((s, i) => (
              <SectionReveal key={i} delay={i * 0.08}>
                <div className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.img} srcSet={srcSetFor(s.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={800} height={600} decoding="async" alt={s.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <s.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold mb-2">{s.title}</h3>
                    <p className="text-muted-foreground text-sm">{s.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
          <SectionReveal delay={0.4}>
            <div className="mt-12 p-4 bg-primary/10 border border-primary/20 rounded-lg text-center max-w-xl mx-auto">
              <p className="text-sm text-foreground">
                <strong>All materials and labor included</strong> — we buy everything and deliver a finished floor.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">Our Work</h2>
            <p className="text-center text-muted-foreground max-w-xl mx-auto mb-8">Real projects from real customers — see the JTB Floors difference.</p>
          </SectionReveal>
          <div className="flex justify-center gap-2 mb-10 flex-wrap">
            {galleryCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryItems
              .filter((item) => {
                if (activeCategory === "All") return true;
                if (item.type === "paired") return item.photos.some(p => p.category === activeCategory);
                return item.category === activeCategory;
              })
              .flatMap((item, i) => {
                if (item.type === "beforeAfter") {
                  return [
                    <SectionReveal key={`${i}-before`} delay={i * 0.05}>
                      <div className="group relative overflow-hidden rounded-xl aspect-[4/3]">
                        <img src={item.before.img} srcSet={srcSetFor(item.before.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={800} height={600} decoding="async" alt={item.before.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute top-3 left-3 bg-foreground/80 text-background px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">Before</div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                          <span className="text-white font-heading font-semibold text-sm">{item.before.caption}</span>
                        </div>
                      </div>
                    </SectionReveal>,
                    <SectionReveal key={`${i}-after`} delay={i * 0.05 + 0.03}>
                      <div className="group relative overflow-hidden rounded-xl aspect-[4/3]">
                        <img src={item.after.img} srcSet={srcSetFor(item.after.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={800} height={600} decoding="async" alt={item.after.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">After</div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                          <span className="text-white font-heading font-semibold text-sm">{item.after.caption}</span>
                        </div>
                      </div>
                    </SectionReveal>,
                  ];
                } else if (item.type === "paired") {
                  return item.photos
                    .filter(p => activeCategory === "All" || p.category === activeCategory)
                    .map((photo, j) => (
                      <SectionReveal key={`${i}-${j}`} delay={i * 0.05 + j * 0.03}>
                        <div className="group relative overflow-hidden rounded-xl aspect-[4/3]">
                          <img src={photo.img} srcSet={srcSetFor(photo.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={800} height={600} decoding="async" alt={photo.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                            <span className="text-white font-heading font-bold text-base">{photo.caption}</span>
                          </div>
                        </div>
                      </SectionReveal>
                    ));
                } else {
                  return [
                    <SectionReveal key={i} delay={i * 0.05}>
                      <div className="group relative overflow-hidden rounded-xl aspect-[4/3]">
                        <img src={item.img} srcSet={srcSetFor(item.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={800} height={600} decoding="async" alt={item.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                          <span className="text-white font-heading font-bold text-base">{item.caption}</span>
                        </div>
                      </div>
                    </SectionReveal>,
                  ];
                }
              })}
          </div>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--background))" />

      {/* Our Guarantee */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <SectionReveal>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-3xl font-heading font-bold">Our Guarantee</h2>
            </div>
            <p className="text-foreground mb-4 leading-relaxed">
              Every flooring project comes with a <strong>100% satisfaction guarantee on all labor</strong>. If you're not happy with the work, we'll make it right — no questions asked.
            </p>
            <blockquote className="border-l-4 border-primary pl-4 my-6 text-lg font-heading font-semibold text-foreground/90 italic">
              "Water damage? We'll still fix the flooring — even if it's not our fault."
            </blockquote>
            <div className="flex flex-wrap gap-4 mt-6">
              {[
                { icon: Award, label: "BBB Accredited" },
                { icon: Shield, label: "Liability Insurance Upon Request" },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 bg-primary/10 rounded-lg px-4 py-2">
                  <badge.icon className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-foreground">{badge.label}</span>
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--secondary))" />

      <CTAStrip headline="Tell us about your flooring project — we'll reply quickly." />
    </Layout>
  );
};

export default Services;
